import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Check } from 'lucide-react'
import { AuthModalField } from '@/components/auth/AuthModalField'
import { AuthPrimaryButton } from '@/components/auth/AuthPrimaryButton'
import { useAuthModal } from '@/context/AuthModalContext'
import { useLoader } from '@/context/LoaderContext'
import { useUsernameAvailability } from '@/hooks/use-username-availability'
import { extractApiErrorMessage, extractApiMessage } from '@/lib/api-message'
import { USERNAME_VALIDATION_MESSAGE, usernameSchema, type UsernameFormValues } from '@/lib/auth-validation'
import { cn } from '@/lib/utils'
import { useSetUsernameMutation } from '@/store/api/authApi'

function getErrorMessage(error: unknown): string | null {
	if (!error || typeof error !== 'object') {
		return null
	}

	const data = 'data' in error ? error.data : error
	return extractApiMessage(data) ?? extractApiErrorMessage(data)
}

export function UsernameOnboardingForm() {
	const { completeUsername } = useAuthModal()
	const { showLoader, hideLoader } = useLoader()
	const [setUsername, { isLoading }] = useSetUsernameMutation()
	const {
		register,
		handleSubmit,
		setValue,
		setError,
		watch,
		formState: { errors },
	} = useForm<UsernameFormValues>({
		resolver: zodResolver(usernameSchema),
		defaultValues: {
			username: '',
			agreedToTerms: false,
		},
	})
	const username = watch('username')
	const agreedToTerms = watch('agreedToTerms')
	const usernameAvailability = useUsernameAvailability(username)

	useEffect(() => {
		if (!isLoading) {
			return
		}

		showLoader()
		return () => hideLoader()
	}, [hideLoader, isLoading, showLoader])

	const onSubmit = async ({ username: submittedUsername }: UsernameFormValues) => {
		const normalizedUsername = submittedUsername.trim()
		if (usernameAvailability.isChecking || !usernameAvailability.isChecked) {
			setError('username', { message: 'Please wait while we check username availability' })
			return
		}

		if (!usernameAvailability.isAvailable) {
			setError('username', { message: usernameAvailability.message ?? 'This username is not available' })
			return
		}

		try {
			await setUsername({ username: normalizedUsername }).unwrap()
			completeUsername()
		} catch (error) {
			setError('username', {
				message: getErrorMessage(error) ?? 'Unable to save your username. Please try again.',
			})
		}
	}

	return (
		<form noValidate className='flex min-h-[360px] translate-y-2 flex-col justify-center gap-4' onSubmit={handleSubmit(onSubmit)}>
			<h2 className='mb-5 text-xl font-semibold tracking-[-0.24px] text-foreground'>Create your account</h2>
			<p className='text-sm leading-[140%] tracking-[-0.21px] text-muted-foreground'>Choose a username to finish setting up your account.</p>
			<AuthModalField
				type='text'
				placeholder='Username'
				autoComplete='username'
				error={errors.username?.message}
				helperText={usernameAvailability.message}
				helperTone={usernameAvailability.tone}
				isChecking={usernameAvailability.isChecking}
				{...register('username')}
			/>
			{!errors.username && username && !usernameAvailability.isChecking && !usernameAvailability.isChecked ? (
				<p className='text-xs leading-[140%] tracking-[-0.18px] text-muted-foreground'>{USERNAME_VALIDATION_MESSAGE}</p>
			) : null}
			<div className='space-y-1.5'>
				<label className='flex cursor-pointer items-start gap-3'>
					<button
						type='button'
						role='checkbox'
						aria-checked={agreedToTerms}
						onClick={() =>
							setValue('agreedToTerms', !agreedToTerms, {
								shouldValidate: true,
							})
						}
						className={cn(
							'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors',
							agreedToTerms ? 'border-primary bg-primary text-background' : 'border-border bg-secondary text-transparent',
						)}
					>
						<Check className='h-3.5 w-3.5' strokeWidth={3} />
					</button>
					<span className='text-sm leading-[140%] tracking-[-0.21px] text-muted-foreground'>
						I&apos;m 18+ years old and I agree to{' '}
						<button type='button' className='text-foreground underline underline-offset-2 transition-opacity hover:opacity-80'>
							Terms &amp; Policy
						</button>{' '}
						of BetsCycle
					</span>
				</label>
				{errors.agreedToTerms?.message ? <p className='text-xs leading-[140%] tracking-[-0.18px] text-destructive'>{errors.agreedToTerms.message}</p> : null}
			</div>
			<AuthPrimaryButton
				label='Create account'
				type='submit'
				isLoading={isLoading}
				disabled={usernameAvailability.isChecking || (usernameAvailability.isChecked && !usernameAvailability.isAvailable)}
			/>
		</form>
	)
}
